import {Injectable} from '@angular/core';

/** Map of sound name to asset URL. */
export type SoundMap = Record<string, string>;

/**
 * Plays short notification sounds through the Web Audio API.
 *
 * Each sound is fetched and decoded once, so playback has no load latency,
 * never depends on an <audio> element's state, and overlapping plays are
 * mixed instead of cutting each other off. The AudioContext starts suspended
 * until the page receives a user gesture; the service listens for the first
 * click, tap or key press and resumes it. A play requested while the context
 * is still locked, or while a sound is still loading, is dropped after a
 * short grace period rather than played late for a scan that has already
 * passed.
 */
@Injectable({providedIn: 'root'})
export class SoundService {
  /** Longest a play request may wait for a decode or unlock before it is dropped. */
  private static readonly MAX_DEFERRED_PLAY_MS = 750;
  private static readonly UNLOCK_EVENTS = ['keydown', 'pointerdown', 'touchend', 'click'];

  private context: AudioContext;
  private buffers = new Map<string, AudioBuffer>();
  /** Pending decodes; each resolves to null (after a console warning) if the load fails. */
  private loading = new Map<string, Promise<AudioBuffer | null>>();
  private unlockListenersAttached = false;
  private readonly unlockHandler = () => this.unlock();

  /** Starts decoding the given sounds and arms the user-gesture unlock. Safe to call repeatedly. */
  public preload(sounds: SoundMap) {
    this.getContext();
    this.attachUnlockListeners();
    Object.entries(sounds).forEach(([name, url]) => this.load(name, url));
  }

  /** Plays a preloaded sound by name. Never throws. */
  public play(name: string) {
    const buffer = this.buffers.get(name);
    if (buffer) {
      this.start(buffer);
      return;
    }
    const pending = this.loading.get(name);
    if (!pending) {
      console.warn(`SoundService: no sound named "${name}" has been preloaded`);
      return;
    }
    const requestedAt = Date.now();
    pending.then(loaded => {
      if (!loaded) {
        return;
      }
      if (Date.now() - requestedAt <= SoundService.MAX_DEFERRED_PLAY_MS) {
        this.start(loaded);
      } else {
        console.warn(`SoundService: "${name}" finished loading too late to play`);
      }
    });
  }

  /** Resumes the AudioContext. Must run from a user gesture to succeed before the page is activated. */
  public unlock(): Promise<void> {
    const context = this.getContext();
    if (!context) {
      return Promise.resolve();
    }
    if (context.state === 'running') {
      this.detachUnlockListeners();
      return Promise.resolve();
    }
    return context
      .resume()
      .then(() => {
        if (context.state === 'running') {
          this.detachUnlockListeners();
        }
      })
      .catch(() => undefined);
  }

  private load(name: string, url: string) {
    if (this.buffers.has(name) || this.loading.has(name)) {
      return;
    }
    const context = this.getContext();
    if (!context) {
      return;
    }
    const promise = fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }
        return response.arrayBuffer();
      })
      .then(data => this.decode(context, data))
      .then(buffer => {
        this.buffers.set(name, buffer);
        this.loading.delete(name);
        return buffer;
      })
      .catch(err => {
        this.loading.delete(name);
        console.warn(`SoundService: could not load "${name}" from ${url}`, err);
        return null;
      });
    this.loading.set(name, promise);
  }

  private decode(context: AudioContext, data: ArrayBuffer): Promise<AudioBuffer> {
    // Callback form for older Safari, which does not return a promise.
    return new Promise((resolve, reject) => context.decodeAudioData(data, resolve, reject));
  }

  private start(buffer: AudioBuffer) {
    const context = this.getContext();
    if (!context) {
      return;
    }
    if (context.state === 'running') {
      this.startSource(context, buffer);
      return;
    }
    // Locked: try to resume, but give up quickly rather than playing a stale sound later.
    const timeout = new Promise<void>(resolve => setTimeout(resolve, SoundService.MAX_DEFERRED_PLAY_MS));
    Promise.race([this.unlock(), timeout]).then(() => {
      if (context.state === 'running') {
        this.startSource(context, buffer);
      } else {
        console.warn('SoundService: audio is locked until the user interacts with the page; sound dropped');
      }
    });
  }

  private startSource(context: AudioContext, buffer: AudioBuffer) {
    try {
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(context.destination);
      source.start();
    } catch (err) {
      console.warn('SoundService: playback failed', err);
    }
  }

  private getContext(): AudioContext {
    if (this.context) {
      return this.context;
    }
    const AudioContextCtor =
      window.AudioContext || (window as Window & {webkitAudioContext?: typeof AudioContext}).webkitAudioContext;
    if (!AudioContextCtor) {
      console.warn('SoundService: Web Audio is not supported in this browser');
      return null;
    }
    this.context = new AudioContextCtor();
    return this.context;
  }

  private attachUnlockListeners() {
    if (this.unlockListenersAttached) {
      return;
    }
    this.unlockListenersAttached = true;
    SoundService.UNLOCK_EVENTS.forEach(event =>
      document.addEventListener(event, this.unlockHandler, {capture: true, passive: true})
    );
  }

  private detachUnlockListeners() {
    if (!this.unlockListenersAttached) {
      return;
    }
    this.unlockListenersAttached = false;
    SoundService.UNLOCK_EVENTS.forEach(event =>
      document.removeEventListener(event, this.unlockHandler, {capture: true})
    );
  }
}
